// The team builder: one pop-up for choosing a team from your whole collection, opened from any screen that fields a
// team (Creatures > Team, the Trail, an Arena line-up, a tournament entry). It sits over the screen, so closing it
// leaves you exactly where you were. The team is cards along the top (tap one, then another to swap, or a creature
// below to put it in that place; ✕ takes it out); your creatures below with search, an element filter and the best of
// each species, strongest first. Save hands the ids back to the screen that opened it.
import type { ArenaCreature } from '../shared/arena.ts';
import type { CreatureDTO } from '../shared/protocol.ts';
import { arenaPower } from '../shared/power.ts';
import { S } from './state.ts';
import { esc, full } from './fmt.ts';
import { synergyChips } from './synergy.ts';
import { pickClick, pickerHtml, pickInput, pickState, pool } from './teampick.ts';
import { fitLists, setFitPage } from './fitlist.ts';

export interface BuilderOpts {
  title: string;
  ids: number[];
  max: number;
  /** One of each species. */
  unique?: boolean;
  /** Never empty (the hunt team). */
  min1?: boolean;
  /** Who may join, and how they would fight; `key` changes when what `ok` depends on does (a level, a tier). */
  name: string;
  key: string;
  ok: (c: CreatureDTO) => boolean;
  asFought: (c: CreatureDTO) => ArenaCreature;
  onSave: (ids: number[]) => void;
}

let open: { o: BuilderOpts; ids: number[]; st: ReturnType<typeof pickState>; scrim: HTMLElement; before: HTMLElement | null } | null = null;

export const builderOpen = () => open !== null;

function teamOf(b: NonNullable<typeof open>): ArenaCreature[] {
  return b.ids.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c).map(b.o.asFought);
}

/** Redraw the team and the grid; the search box stays as it is so typing keeps its focus. */
function draw(full0 = false) {
  const b = open;
  if (!b) return;
  const team = teamOf(b);
  const items = pool(b.o.name, b.o.key, b.o.ok, b.o.asFought);
  const pk = pickerHtml({ act: 'tb', st: b.st, team, max: b.o.max, items, unique: b.o.unique });
  const box = b.scrim.firstElementChild as HTMLElement;
  if (full0) {
    box.innerHTML = `<div class="tb-head"><h3 id="tb-title">${esc(b.o.title)}</h3><span class="spacer"></span><button class="bv-btn" data-tb="cancel">Cancel</button><button class="bv-btn main" data-tb="save">Save</button></div>
      <div class="tb-team"></div><div class="tb-tools">${pk.tools}</div><div class="tb-grid"></div>`;
  }
  const power = team.reduce((a, c) => a + arenaPower(c), 0);
  box.querySelector('.tb-team')!.innerHTML = `<div class="tn-sec"><h4>Team <span class="muted">${team.length}/${b.o.max} · Power ${full(power)}</span></h4>${synergyChips(team)}</div>${pk.strip}`;
  const g = box.querySelector('.tb-grid')!;
  g.innerHTML = pk.grid;
  // Full with no place picked: the grid waits, dimmed, until you tap a team member.
  g.classList.toggle('wait', team.length >= b.o.max && b.st.pick === null);
  // The element select and Best toggle live in the tools row: keep them in step without replacing the search box.
  const best = box.querySelector<HTMLButtonElement>('[data-act="tb-best"]');
  if (best) { best.classList.toggle('on', !b.st.all); best.setAttribute('aria-pressed', String(!b.st.all)); }
  (box.querySelector('[data-tb="save"]') as HTMLButtonElement).disabled = !team.length;
  // The creatures page to fit the dialog instead of scrolling inside it (no scrolling anywhere, Cole 2026-10-27).
  fitLists(box);
}

const refit = () => { if (open) fitLists(open.scrim.firstElementChild as HTMLElement); };

function close(save: boolean) {
  const b = open;
  if (!b) return;
  open = null;
  document.removeEventListener('keydown', onKey, true);
  removeEventListener('resize', refit);
  b.scrim.remove();
  b.before?.focus?.();
  if (save) b.o.onSave(b.ids.filter((id) => S.creatures.has(id)));
}

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); close(false); }
}

export function openTeamBuilder(o: BuilderOpts) {
  if (open) close(false);
  const scrim = document.createElement('div');
  scrim.className = 'scrim tb-scrim';
  scrim.innerHTML = '<div class="dialog tb" role="dialog" aria-modal="true" aria-labelledby="tb-title"></div>';
  document.body.appendChild(scrim);
  open = { o, ids: [...o.ids], st: pickState(), scrim, before: document.activeElement as HTMLElement | null };
  draw(true);
  document.addEventListener('keydown', onKey, true);
  addEventListener('resize', refit);
  scrim.addEventListener('click', (e) => {
    if (e.target === scrim) return close(false);
    const t = e.target as HTMLElement;
    const go = t.closest<HTMLElement>('[data-fit-go]');
    if (go) {
      e.stopPropagation();
      const [id, n] = go.dataset.fitGo!.split(':');
      setFitPage(id, Number(n));
      return refit();
    }
    const btn = t.closest<HTMLElement>('[data-tb]');
    if (btn) return close(btn.dataset.tb === 'save' && !(btn as HTMLButtonElement).disabled);
    const a = t.closest<HTMLElement>('[data-act]');
    if (!a || !open) return;
    e.stopPropagation();
    if (pickClick(a.dataset.act ?? '', 'tb', a.dataset.id ?? '', open.st, open.ids, o.max, o.min1)) draw();
  });
  const onIn = (e: Event) => { if (open && pickInput(e.target as HTMLInputElement, 'tb', open.st)) draw(); };
  scrim.addEventListener('input', onIn);
  scrim.addEventListener('change', (e) => { if ((e.target as HTMLElement).tagName === 'SELECT') onIn(e); });
  (scrim.querySelector('[data-tb="save"]') as HTMLElement | null)?.focus({ preventScroll: true });
}
